import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Alert,
  Chip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import AppHeader from "./AppHeader";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// FIX: We no longer need to import these unused assets since we are using
// the CDN link for the default icon below.
// import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
// import iconUrl from 'leaflet/dist/images/marker-icon.png';
// import shadowUrl from 'leaflet/dist/images/marker-shadow.png';


const API_BASE =
  process.env.REACT_APP_EMERGENCY_API_URL ||
  "http://localhost:3000/api/emergency";

// ========================================
// FIX LEAFLET MARKER ICON ISSUE (explicit icon via CDN)
// We must define this outside the component to prevent constant recreation.
// ========================================
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});
// ========================================

// Component to recenter map when location changes
function RecenterMap({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      console.log("🗺️ Map recentering to:", lat, lng);
      map.setView([lat, lng], 16);
    }
  }, [lat, lng, map]);
  return null;
}

// Helper to fix map size
function FixMapSize() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);
  return null;
}

function HotEventHistory({ setIsLoggedIn }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    status: "",
    page: 1,
    pageSize: 50,
  });

  const [selectedLocation, setSelectedLocation] = useState(null);

  // Note: Removed the unused useEffect block that was merging icon options, 
  // as the icon is now defined and used explicitly below.

  // Column Definitions
  const columnDefs = [
    {
      headerName: "Event ID",
      field: "event_id",
      width: 110,
      cellRenderer: (params) => (
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/emergency/hot/${params.value}`);
          }}
        >
          #{params.value}
        </Button>
      ),
    },
    { headerName: "Type", field: "type", width: 130 },
    { 
      headerName: "Severity", 
      field: "severity", 
      width: 120,
      cellRenderer: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'critical' ? 'error' : 'warning'} 
          size="small"
        />
      )
    },
    { headerName: "Scope", field: "scope", width: 160 },
    { 
      headerName: "Last Lat", 
      field: "last_lat", 
      width: 120,
      valueFormatter: (params) => {
        return params.value != null ? parseFloat(params.value).toFixed(6) : "N/A";
      }
    },
    { 
      headerName: "Last Lng", 
      field: "last_lng", 
      width: 120,
      valueFormatter: (params) => {
        return params.value != null ? parseFloat(params.value).toFixed(6) : "N/A";
      }
    },
    { 
      headerName: "Status", 
      field: "status", 
      width: 110,
      cellRenderer: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'open' ? 'success' : 'default'} 
          size="small"
        />
      )
    },
    {
      headerName: "Started",
      field: "started_at_fmt",
      width: 180,
    },
    {
      headerName: "Closed",
      field: "closed_at_fmt",
      width: 180,
    },
    { headerName: "Triggered By", field: "full_name", width: 150 },
    { headerName: "Dept", field: "department", width: 100 },
  ];

  // Format date/time
  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Yangon",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Parse Coordinates (handles strings)
  const parseCoordinate = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    
    const cleaned = String(value).trim();
    const parsed = parseFloat(cleaned);
    
    if (isNaN(parsed)) {
      console.error("Failed to parse coordinate:", value);
      return null;
    }
    
    return parsed;
  };

  // Fetch backend data
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.status) params.append("status", filters.status);

      params.append("page", filters.page);
      params.append("pageSize", filters.pageSize);

    const tenant = localStorage.getItem("tenant") || "";

    const res = await fetch(
      `${API_BASE}/admin/hot/history?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "x-tenant": tenant,
        },
      }
    );

      const json = await res.json();

      if (!json.success) {
        alert("Failed to load history: " + (json.message || "Unknown error"));
        return;
      }

      const rows = json.rows.map((item) => ({
        ...item,
        status: item.event_status,
        started_at_fmt: formatDate(item.started_at),
        closed_at_fmt: formatDate(item.closed_at),
      }));

      setRowData(rows);
      setTotal(json.total);
    } catch (err) {
      console.error("❌ History load error:", err);
      alert(`Error loading emergency history: ${err.message || 'Check console'}`); 
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Handlers
  const handleChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleReset = () => {
    setFilters({
      date_from: "",
      date_to: "",
      status: "",
      page: 1,
      pageSize: 50,
    });
  };

  // Row Click Handler 
  const handleRowClick = (e) => {
    console.log("🔍 Row clicked:", e.data);

    const rawLat = e.data.last_lat;
    const rawLng = e.data.last_lng;

    console.log("📊 Raw coordinates:", { rawLat, rawLng });

    const lat = parseCoordinate(rawLat);
    const lng = parseCoordinate(rawLng);

    console.log("🧮 Parsed coordinates:", { lat, lng });

    if (lat === null || lng === null) {
      console.warn("❌ No valid coordinates");
      alert("⚠️ This event has no valid location data");
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.error("❌ Coordinates out of valid range");
      alert(`⚠️ Invalid coordinates: ${lat}, ${lng}`);
      return;
    }

    console.log("✅ Valid location found:", { lat, lng });

    const locationData = {
      lat,
      lng,
      name: e.data.full_name || "Unknown User",
      eventId: e.data.event_id,
      type: e.data.type,
      severity: e.data.severity,
      status: e.data.status,
      department: e.data.department,
      staffCode: e.data.staff_code,
      startedAt: e.data.started_at_fmt,
      closedAt: e.data.closed_at_fmt,
    };

    setSelectedLocation(locationData);
  };

  // Render
  return (
    <Box p={3}>
      <AppHeader onLogout={() => setIsLoggedIn(false)} />

      <Typography variant="h5" gutterBottom>
        🚨 Hot Button Emergency History
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.date_from}
            onChange={(e) => handleChange("date_from", e.target.value)}
            size="small"
          />

          <TextField
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.date_to}
            onChange={(e) => handleChange("date_to", e.target.value)}
            size="small"
          />

          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) => handleChange("status", e.target.value)}
            sx={{ width: 150 }}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>

          <Button variant="contained" onClick={fetchHistory} size="small">
            Apply
          </Button>

          <Button variant="outlined" onClick={handleReset} size="small">
            Reset
          </Button>
        </Box>
      </Paper>

      {/* AG Grid */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        📊 Total Records: <strong>{total}</strong>
        {selectedLocation && (
          <Chip 
            label="📍 Location Selected - See Map Below" 
            color="primary" 
            sx={{ ml: 2 }}
            onDelete={() => setSelectedLocation(null)}
          />
        )}
      </Typography>

      <Paper elevation={3} sx={{ mb: 3 }}>
        <div
          className="ag-theme-alpine"
          style={{ height: "55vh", width: "100%" }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={filters.pageSize}
            suppressCellFocus={true}
            loading={loading}
            animateRows={true}
            // WARNING FIX: Use rowSelection='single' object notation
            rowSelection="single" 
            onRowClicked={handleRowClick}
          />
        </div>
      </Paper>

      {/* Map Display */}
      {selectedLocation && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              📍 Last Known Location
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => setSelectedLocation(null)}
            >
              ✕ Close Map
            </Button>
          </Box>

          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            <Chip icon={<span>👤</span>} label={selectedLocation.name} />
            <Chip icon={<span>🆔</span>} label={selectedLocation.staffCode} />
            <Chip icon={<span>🏢</span>} label={selectedLocation.department} />
            <Chip 
              icon={<span>🚨</span>} 
              label={`Event #${selectedLocation.eventId}`}
              color="primary"
            />
            <Chip 
              label={selectedLocation.severity} 
              color={selectedLocation.severity === 'critical' ? 'error' : 'warning'}
            />
            <Chip 
              label={selectedLocation.status} 
              color={selectedLocation.status === 'open' ? 'success' : 'default'}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>📌 Coordinates:</strong> {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
            </Typography>
            <Typography variant="body2">
              <strong>⏰ Started:</strong> {selectedLocation.startedAt}
              {selectedLocation.closedAt && selectedLocation.closedAt !== '-' && (
                <> | <strong>Closed:</strong> {selectedLocation.closedAt}</>
              )}
            </Typography>
          </Alert>

          <Box
            sx={{
              height: "500px",
              width: "100%",
              borderRadius: 2,
              overflow: "visible",
              border: "2px solid #1976d2",
            }}
          >
            {console.log("🗺️ Rendering MapContainer with location:", selectedLocation)}
            <MapContainer
              center={[selectedLocation.lat, selectedLocation.lng]}
              zoom={16}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker
                position={[selectedLocation.lat, selectedLocation.lng]}
                icon={defaultIcon} // FIX: Explicitly use the defined defaultIcon
              >
                <Popup>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>🚨 {selectedLocation.name}</strong>
                    </Typography>
                    <Typography variant="caption" display="block">
                      Event ID: #{selectedLocation.eventId}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Staff: {selectedLocation.staffCode}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Department: {selectedLocation.department}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Type: {selectedLocation.type}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Severity: {selectedLocation.severity}
                    </Typography>
                    <Typography variant="caption" display="block" color="primary" sx={{ mt: 1 }}>
                      📍 {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                    </Typography>
                  </Box>
                </Popup>
              </Marker>
              <FixMapSize />
              <RecenterMap lat={selectedLocation.lat} lng={selectedLocation.lng} />
            </MapContainer>
          </Box>

          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const url = `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`;
                window.open(url, '_blank');
              }}
            >
              🗺️ Open in Google Maps
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const coords = `${selectedLocation.lat}, ${selectedLocation.lng}`;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(coords);
                    alert('Coordinates copied to clipboard!');
                } else {
                    const textarea = document.createElement('textarea');
                    textarea.value = coords;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    alert('Coordinates copied to clipboard!');
                }
              }}
            >
              📋 Copy Coordinates
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default HotEventHistory;